import type { EligibilitySessionDto } from '@his/shared';
import type { BadgeVariant } from '@/components/ui/badge';

export type ValidityLabelKey = 'nx_eligibility_valid_until' | 'nx_eligibility_expired_on' | 'nx_eligibility_never_checked';

export interface ValidityLabel {
  key: ValidityLabelKey;
  date: string | null;
}

type SessionFacts = Pick<EligibilitySessionDto, 'state' | 'validUntil'>;

export function validityLabel(session: SessionFacts | null | undefined): ValidityLabel {
  if (!session || session.state === 'never') return { key: 'nx_eligibility_never_checked', date: null };
  if (session.state === 'valid') return { key: 'nx_eligibility_valid_until', date: session.validUntil };
  return { key: 'nx_eligibility_expired_on', date: session.validUntil };
}

export type RemainingUnit = 'days' | 'hours' | 'minutes';

export interface Remaining {
  unit: RemainingUnit;
  count: number;
}

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86_400;

export function remainingOf(seconds: number): Remaining | null {
  if (!(seconds > 0)) return null;
  if (seconds >= DAY) return { unit: 'days', count: Math.floor(seconds / DAY) };
  if (seconds >= HOUR) return { unit: 'hours', count: Math.floor(seconds / HOUR) };
  return { unit: 'minutes', count: Math.max(1, Math.floor(seconds / MINUTE)) };
}

export function remainingKey(unit: RemainingUnit): 'nx_remaining_days' | 'nx_remaining_hours' | 'nx_remaining_minutes' {
  if (unit === 'days') return 'nx_remaining_days';
  if (unit === 'hours') return 'nx_remaining_hours';
  return 'nx_remaining_minutes';
}

export interface EligibilityPlan {
  primary: 'authorise' | 'check' | 'discovery';
  canExpire: boolean;
  secondaryDiscovery: boolean;
}

export function eligibilityPlan(input: { hasPolicy: boolean; session: EligibilitySessionDto | null }): EligibilityPlan {
  const { hasPolicy, session } = input;
  if (!hasPolicy) return { primary: 'discovery', canExpire: false, secondaryDiscovery: false };
  if (session?.canAuthoriseDirectly) return { primary: 'authorise', canExpire: session.state === 'valid', secondaryDiscovery: false };
  return { primary: 'check', canExpire: session?.state === 'valid', secondaryDiscovery: session?.inForce === false };
}

export interface EligibilityChipModel {
  variant: BadgeVariant;
  key: 'nx_eligibility_state_valid' | 'nx_eligibility_state_expired' | 'nx_eligibility_state_never' | 'nx_eligibility_not_in_force';
}

export function eligibilityChip(session: Pick<EligibilitySessionDto, 'state' | 'inForce'> | null | undefined): EligibilityChipModel {
  if (!session || session.state === 'never') return { variant: 'muted', key: 'nx_eligibility_state_never' };
  if (session.inForce === false) return { variant: 'destructive', key: 'nx_eligibility_not_in_force' };
  if (session.state === 'valid') return { variant: 'success', key: 'nx_eligibility_state_valid' };
  return { variant: 'warning', key: 'nx_eligibility_state_expired' };
}

export function parseValidDays(raw: string): number | null {
  if (!/^\d{1,4}$/.test(raw.trim())) return null;
  const value = Number.parseInt(raw, 10);
  return value >= 1 && value <= 365 ? value : null;
}

export function pickSession(items: readonly EligibilitySessionDto[] | undefined): EligibilitySessionDto | null {
  if (!items || items.length === 0) return null;
  return items.find((item) => item.canAuthoriseDirectly) ?? items.find((item) => item.state === 'valid') ?? items[0] ?? null;
}

export function limitKey(limitedBy: EligibilitySessionDto['limitedBy']): 'nx_limit_service_period' | 'nx_limit_days' | 'nx_limit_year_end' | null {
  if (limitedBy === 'servicedPeriodEnd') return 'nx_limit_service_period';
  if (limitedBy === 'checkedPlusDays') return 'nx_limit_days';
  if (limitedBy === 'endOfYear') return 'nx_limit_year_end';
  return null;
}
